import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMoney } from "../../hooks/useMoney";
import { useBroadwayMusicalSeat } from "../../services/musicalService";
import { SelectTagArea } from "../detail/SelectTagArea";
import Icon from "../image/Icon";
import { icClose } from "../image/image-src";
import NetworkImage from "../image/NetworkImage";
import Spin from "../spin/Spin";

export interface ISeatSelected {
  product: string;
  session: string;
  ticketTypes: TicketTypes;
}

interface TicketTypes {
  ticketType: string;
  seat: Seat;
}

interface Seat {
  productId: string;
  price: string;
  numberTickets: string;
  area: string;
  lowSeatNumber: string;
  highSeatNumber: string;
  row: string;
}

interface SelectSeatProps {
  onClose?: () => void;
  onSubmit?: (value: ISeatSelected) => void;
  showCode: string;
  eventDateTime: string;
  quantity: number;
  d: ProductDetailResponse;
  ticketTypeDefault?: string;
  seatSelectedDefault?: string;
}

const ProdAttrOfMusicalSeat: React.FC<SelectSeatProps> = ({
  onClose,
  onSubmit,
  showCode,
  eventDateTime,
  quantity,
  d,
  ticketTypeDefault,
  seatSelectedDefault,
}) => {
  const [ticketType, setTicketType] = useState<string>(ticketTypeDefault || "");
  const [seatSelected, setSeatSelected] = useState<string>(
    seatSelectedDefault || "",
  );
  const { t } = useTranslation();
  const { formatMoney } = useMoney();

  const {
    data: musicalSeat,
    isLoading,
    isError,
  } = useBroadwayMusicalSeat({
    showCode,
    eventDateTime,
    quantity,
  });

  const seats = musicalSeat?.ticketTypes.reduce(
    (prev, cur) => ({ ...prev, [cur.ticketType]: cur.seats }),
    {} as {
      [key: string]: Seat[];
    },
  );

  const handleApply = () => {
    const seat = seats?.[ticketType].find(
      (item) => item.productId === seatSelected,
    );

    const data = {
      product: musicalSeat?.product,
      session: musicalSeat?.session,
      ticketTypes: {
        ticketType: ticketType,
        seat,
      },
    };
    onSubmit?.(data as ISeatSelected);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative flex max-h-[95dvh] w-full max-w-[460px] flex-col overflow-hidden rounded-[1rem] bg-white shadow-lg">
        <div className="relative aspect-[3/1] shrink-0 overflow-hidden">
          <Icon
            src={icClose}
            className="absolute right-2.5 top-2.5 size-6"
            onClick={onClose}
          />
          <NetworkImage
            src={d.thumbnail}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="grid gap-4 overflow-auto px-6 py-5">
          <div className="grid gap-2">
            <p className="grid h-9 w-max place-items-center rounded bg-[#F7F8FA] px-2 text-xs text-[#5E6D77]">
              {t("Musical")}
            </p>
            <p className="text-base font-semibold text-[#232323]">{d.name}</p>
            {/* <RichTextBox richtext={d?.information} /> */}
          </div>
          {isError || !quantity ? (
            <p className="text-red-500">
              *{t("An error occurred or no quantity was selected")}
            </p>
          ) : (
            <Spin
              spinning={isLoading}
              tip={
                <Trans
                  i18nKey={t(
                    "Checking available ticket dates. Please do not refresh or go back",
                  )}
                />
              }
              className="!h-max min-h-28"
            >
              <SelectTagArea
                title={t("Selected ticket type")}
                options={
                  musicalSeat?.ticketTypes.map((item) => ({
                    displayName: item.ticketType,
                    value: item.ticketType,
                  })) || []
                }
                maxSelectable={1}
                defaultSelected={[ticketType]}
                onSelect={(options) => {
                  setTicketType(options[0]?.value);
                  setSeatSelected("");
                }}
                isCheckbox={false}
              />
              {ticketType && (
                <SelectTagArea
                  options={
                    seats?.[ticketType as keyof typeof seats]?.map((item) => ({
                      displayName: `${item.area} - ${item.row}${item.lowSeatNumber}-${item.highSeatNumber} (${formatMoney(+item.price)})`,
                      value: item.productId,
                    })) || []
                  }
                  defaultSelected={[seatSelected]}
                  onSelect={(options) => setSeatSelected(options[0]?.value)}
                  title={t("Select seat")}
                  isCheckbox={false}
                />
              )}
            </Spin>
          )}

          <div>
            <p className="mb-4 flex flex-wrap items-center gap-2 text-sm font-normal text-[#5E6D77]">
              {t("Seating information")}
            </p>
            <NetworkImage src={d?.locationInstruction?.[0]?.src} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-[3.4375rem] border border-[#232323] py-[0.8125rem] text-base font-semibold leading-[1.25rem] text-[#232323] hover:bg-gray-100 disabled:pointer-events-none"
            // disabled={!canDeselect}
          >
            {t("Deselect")}
          </button>
          <button
            type="button"
            disabled={!ticketType && !seatSelected}
            onClick={handleApply}
            className={`${
              ticketType && seatSelected
                ? "bg-primary text-[#232323]"
                : "bg-[#DCDCDC] text-white"
            } popup-apply--btn w-full rounded-[3.4375rem] py-[0.8125rem] text-base font-semibold leading-[1.25rem] text-[#232323]`}
          >
            {t("Apply")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProdAttrOfMusicalSeat;
